import { createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, unref as _unref, vShow as _vShow, withDirectives as _withDirectives, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "timetable" }
const _hoisted_2 = { class: "card mb-3" }
const _hoisted_3 = { class: "card-body" }
const _hoisted_4 = { class: "row g-3" }
const _hoisted_5 = { class: "form-label mb-1" }
const _hoisted_6 = { class: "mt-3 d-flex align-items-center gap-1" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["disabled"]
const _hoisted_9 = {
  key: 0,
  class: "text-muted"
}
const _hoisted_10 = {
  key: 1,
  class: "text-muted"
}
const _hoisted_11 = { class: "nav nav-tabs mb-3" }
const _hoisted_12 = { class: "nav-item" }
const _hoisted_13 = { class: "nav-item" }
const _hoisted_14 = {
  key: 0,
  class: "badge text-bg-danger ms-1"
}
const _hoisted_15 = {
  key: 0,
  class: "alert alert-info"
}
const _hoisted_16 = {
  key: 1,
  class: "text-muted small mb-2"
}

import { ref, reactive, computed, watch, nextTick } from "vue";
import FullCalendar from "@fullcalendar/vue3";
import timeGridPlugin from "@fullcalendar/timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import deLocale from "@fullcalendar/core/locales/de";
import CalendarDatePicker from "../../room-booking-management/calendar/components/CalendarDatePicker.vue";
import ConflictOverview from "./components/ConflictOverview.vue";
import { EMPTY_CONFLICT_SUMMARY, fetchConflicts, fetchEvents, fetchFilterOptions } from "./api";

const EVENT_COLOR_BOOKED = "#198754"; // gebucht – confirmed (Bootstrap success green)
const EVENT_COLOR_PLANNED = "#0d6efd"; // geplant – planning stage (Bootstrap primary blue)


export default {
  __name: 'TimetableApp',
  props: {
	apiUrl: { type: String, required: true },
},
  setup(__props) {

const props = __props;

const activeTab = ref("calendar");

const filterDefs = [
	{ key: "degrees", label: "Abschluss" },
	{ key: "studyTypes", label: "Studienfach" },
	{ key: "specializations", label: "Vertiefung" },
	{ key: "typicalSemesters", label: "Fachsemester" },
	{ key: "electiveArea", label: "Wahlbereich" },
	{ key: "lecturers", label: "Dozent*in" },
	{ key: "rooms", label: "Raum" },
];

const options = reactive({
	degrees: [],
	studyTypes: [],
	specializations: [],
	typicalSemesters: [],
	electiveArea: [],
	lecturers: [],
	rooms: [],
});

const selected = reactive({
	degrees: [],
	studyTypes: [],
	specializations: [],
	typicalSemesters: [],
	electiveArea: [],
	lecturers: [],
	rooms: [],
});

const events = ref([]);
const loading = ref(false);
// The filter set that was active when "Laden" was last clicked (null = nothing loaded yet).
const appliedFilters = ref(null);
// The currently visible calendar date range.
const currentRange = ref(null);

// Conflict overview (Konflikte tab).
const conflicts = ref([]);
const conflictSummary = ref({ ...EMPTY_CONFLICT_SUMMARY });
const conflictsLoading = ref(false);

// --- Filter options ---------------------------------------------------------
async function loadFilterOptions() {
	try {
		const loaded = await fetchFilterOptions(props.apiUrl);
		for (const key of Object.keys(options)) {
			options[key] = loaded[key] || [];
		}
	} catch (error) {
		console.error("Failed to load timetable filter options", error);
	}
}
loadFilterOptions();

function buildFilters() {
	return {
		degrees: selected.degrees.map((o) => o.value),
		studyTypes: selected.studyTypes.map((o) => o.value),
		specializations: selected.specializations.map((o) => o.value),
		typicalSemesters: selected.typicalSemesters.map((o) => o.value),
		electiveArea: selected.electiveArea.map((o) => o.value),
		lecturers: selected.lecturers.map((o) => o.value),
		rooms: selected.rooms.map((o) => o.value),
	};
}

function resetFilters() {
	for (const key of Object.keys(selected)) {
		selected[key] = [];
	}
}

// --- Tabs --------------------------------------------------------------------
function setTab(tab) {
	activeTab.value = tab;
	if (tab === "conflicts") {
		// A no-op while the conflicts already match the current filters/range; only a previously
		// failed or never-run scan actually reloads here.
		loadConflicts();
	}
}

// --- Events -----------------------------------------------------------------
function loadWithCurrentFilters() {
	appliedFilters.value = buildFilters();
	loadEvents();
	// Conflicts feed both the "Konflikte" tab and the calendar's conflict markings, so they are always
	// refreshed – loadConflicts() itself skips the call when nothing relevant changed.
	loadConflicts();
}

// Severity order used to order an event's conflict levels deterministically (room, then lecturer,
// then cohort) so the resulting CSS combo class (e.g. tt-conflict--room-lecturer) is stable and
// matches the pre-defined gradient rules.
const CONFLICT_SEVERITY = { room: 3, lecturer: 2, cohort: 1 };

// Maps each appointment id to the set of conflict levels it is involved in, derived from the real
// backend conflicts (the same data the "Konflikte" tab shows) rather than a naive time overlap.
const conflictLevelsByAppointment = computed(() => {
	const map = new Map();
	for (const conflict of conflicts.value) {
		for (const side of [conflict.a, conflict.b]) {
			if (side && side.kind === "appointment" && side.appointmentId) {
				const id = String(side.appointmentId);
				if (!map.has(id)) {
					map.set(id, new Set());
				}
				map.get(id).add(conflict.level);
			}
		}
	}
	return map;
});

// The conflict CSS classes for a given appointment id ([] when it has no conflict). Several conflict
// levels are ordered by severity and joined into one combo class (e.g. tt-conflict--room-lecturer)
// whose border is split into one coloured segment per level.
function conflictClassesFor(id) {
	const levels = conflictLevelsByAppointment.value.get(String(id));
	if (!levels || levels.size === 0) {
		return [];
	}
	const ordered = [...levels].sort(
		(a, b) => (CONFLICT_SEVERITY[b] || 0) - (CONFLICT_SEVERITY[a] || 0),
	);
	return ["tt-conflict", `tt-conflict--${ordered.join("-")}`];
}

// Apply the conflict markings imperatively through the FullCalendar API. Passing an updated events
// array via :options does not reliably re-render already-rendered events (same id) with new class
// names, and the conflicts load asynchronously after the events – so, like the room-booking calendar,
// we drive the change through the calendar API instead.
function applyConflictClasses() {
	const api = calendar.value?.getApi();
	if (!api) {
		return;
	}
	for (const event of api.getEvents()) {
		const desired = conflictClassesFor(event.id);
		const current = event.classNames || [];
		const changed =
			desired.length !== current.length || desired.some((cls, i) => cls !== current[i]);
		if (changed) {
			event.setProp("classNames", desired);
		}
	}
}

// Re-apply the markings whenever the events (new range/filter) or the conflicts change. nextTick lets
// FullCalendar finish rendering the new events first, so getEvents() returns the current set.
watch([events, conflicts], () => {
	nextTick(applyConflictClasses);
});

// The filter/range combination the loaded conflicts belong to. The conflict scan is expensive (it
// covers the whole lecture period), so it must not re-run for data we already have – e.g. every time
// the "Konflikte" tab is opened.
const loadedConflictsKey = ref(null);

function conflictsKey() {
	return appliedFilters.value && currentRange.value
		? JSON.stringify([appliedFilters.value, currentRange.value])
		: null;
}

let latestConflictsRequest = 0;

async function loadConflicts() {
	const key = conflictsKey();
	if (!key || key === loadedConflictsKey.value) {
		return;
	}

	const requestId = ++latestConflictsRequest;
	conflictsLoading.value = true;
	try {
		const data = await fetchConflicts(props.apiUrl, {
			start: currentRange.value.start,
			end: currentRange.value.end,
			filters: appliedFilters.value,
		});
		if (requestId !== latestConflictsRequest) {
			return;
		}
		conflicts.value = data.conflicts;
		conflictSummary.value = data.summary;
		loadedConflictsKey.value = key;
	} catch (error) {
		if (requestId !== latestConflictsRequest) {
			return;
		}
		console.error("Failed to load timetable conflicts", error);
		conflicts.value = [];
		conflictSummary.value = { ...EMPTY_CONFLICT_SUMMARY };
		// Leave the key unset so the next trigger retries instead of trusting the empty result.
		loadedConflictsKey.value = null;
	} finally {
		if (requestId === latestConflictsRequest) {
			conflictsLoading.value = false;
		}
	}
}

// Background colours by booking status: planned appointments (still being scheduled) vs. those
// already handed over to the room booking system. Set as per-event colours (not CSS classes) so they
// don't collide with the imperative conflict-class handling. All-day markers (Vorlesungspause /
// Feiertage) carry no `booked` flag and keep their own .nt-marker styling.
function styleEventByStatus(event) {
	if (typeof event.booked !== "boolean") {
		return event;
	}
	const color = event.booked ? EVENT_COLOR_BOOKED : EVENT_COLOR_PLANNED;
	return { ...event, backgroundColor: color, borderColor: color };
}

// Guards against out-of-order responses: switching filters or navigating the calendar quickly can let
// an earlier request resolve after a later one and overwrite the newer data with stale results. Only
// the most recent request of each kind is allowed to write its result.
let latestEventsRequest = 0;

async function loadEvents() {
	if (!appliedFilters.value || !currentRange.value) {
		return;
	}

	const requestId = ++latestEventsRequest;
	loading.value = true;
	try {
		const loaded = await fetchEvents(props.apiUrl, {
			start: currentRange.value.start,
			end: currentRange.value.end,
			filters: appliedFilters.value,
		});
		if (requestId !== latestEventsRequest) {
			return;
		}
		events.value = loaded.map(styleEventByStatus);
	} catch (error) {
		if (requestId !== latestEventsRequest) {
			return;
		}
		console.error("Failed to load timetable events", error);
		events.value = [];
	} finally {
		if (requestId === latestEventsRequest) {
			loading.value = false;
		}
	}
}

function handleDatesSet(info) {
	currentRange.value = {
		start: info.start.toISOString(),
		end: info.end.toISOString(),
	};
	// Reload events for the new range, but only once a filter set has been applied. Conflicts are
	// reloaded too so the calendar markings stay in sync when navigating without a catalog semester
	// (where the conflict scan follows the visible range rather than the whole lecture period).
	if (appliedFilters.value) {
		loadEvents();
		loadConflicts();
	}
}

// Jump from a conflict in the overview to its date in the calendar.
function handleJumpToDate(date) {
	if (!date) {
		return;
	}
	setTab("calendar");
	calendar.value?.getApi()?.gotoDate(date);
}

// --- Hover pop-out -----------------------------------------------------------
function escapeHtml(value) {
	if (value === null || value === undefined) {
		return "";
	}
	return String(value)
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

function row(icon, content) {
	return (
		`<div style="display: flex; align-items: flex-start; margin-bottom: 4px; color: #e6e6e6;">` +
		`<i class="fa-solid ${icon}" style="width: 22px; flex-shrink: 0;"></i>` +
		`<div>${content}</div></div>`
	);
}

function showTooltip(info) {
	hideTooltip();
	const e = info.event;
	// All-day markers (Vorlesungspause / Feiertage / vorlesungsfrei) carry no course data – no tooltip.
	if (e.allDay) {
		return;
	}
	const p = e.extendedProps;

	let html =
		`<div style="font-weight: bold; margin-bottom: 6px; color: #fff;">` +
		`${escapeHtml(p.courseNumber ? p.courseNumber + ": " : "")}${escapeHtml(e.title)}</div>`;

	if (p.lecturer) {
		html += row("fa-chalkboard-user", escapeHtml(p.lecturer));
	}

	const startTime = e.start
		? new Date(e.start).toLocaleString("de-DE", {
				weekday: "short",
				hour: "2-digit",
				minute: "2-digit",
		  })
		: "";
	const endTime = e.end
		? new Date(e.end).toLocaleTimeString("de-DE", {
				hour: "2-digit",
				minute: "2-digit",
		  })
		: "";
	html += row("fa-clock", `${startTime} - ${endTime} Uhr`);

	html += row(
		"fa-location-dot",
		p.room ? escapeHtml(p.room) : "<em>kein Raum</em>"
	);

	if (p.description) {
		html += row("fa-comment", escapeHtml(p.description));
	}

	if (Array.isArray(p.participations) && p.participations.length) {
		const items = p.participations
			.map((item) => `<div>${escapeHtml(item)}</div>`)
			.join("");
		html += row("fa-users", items);
	}

	const tooltip = document.createElement("div");
	tooltip.id = "timetable-tooltip";
	tooltip.innerHTML = html;
	tooltip.style.cssText = `
		position: absolute;
		background: rgba(0, 0, 0, 0.95);
		color: white;
		padding: 10px 14px;
		border-radius: 8px;
		font-size: 13px;
		line-height: 1.5;
		z-index: 1080;
		max-width: 320px;
		overflow-y: auto;
		box-shadow: 0 4px 12px rgba(0,0,0,0.4);
		pointer-events: auto;
		white-space: normal;
		word-wrap: break-word;
	`;

	// Keep the tooltip open while the pointer is over it (so a long, scrollable
	// list can actually be scrolled), and close it once the pointer leaves.
	tooltip.addEventListener("mouseenter", cancelHideTooltip);
	tooltip.addEventListener("mouseleave", hideTooltip);

	document.body.appendChild(tooltip);

	const margin = 10;
	const rect = info.el.getBoundingClientRect();
	const viewportWidth = window.innerWidth;
	const viewportHeight = window.innerHeight;

	// Decide whether to place the tooltip above or below the event based on the
	// available space, and cap its height to that space so it never leaves the
	// viewport (e.g. for the first event of the day, where there is no room above).
	const spaceAbove = rect.top - margin;
	const spaceBelow = viewportHeight - rect.bottom - margin;
	const placeAbove = spaceAbove >= spaceBelow;
	tooltip.style.maxHeight = Math.max(0, placeAbove ? spaceAbove : spaceBelow) + "px";

	const tooltipRect = tooltip.getBoundingClientRect();

	let left = rect.left + window.scrollX + rect.width / 2;
	if (left - tooltipRect.width / 2 < 10) {
		left = tooltipRect.width / 2 + 10;
	} else if (left + tooltipRect.width / 2 > viewportWidth - 10) {
		left = viewportWidth - tooltipRect.width / 2 - 10;
	}

	if (placeAbove) {
		tooltip.style.transform = "translateX(-50%) translateY(-100%)";
		tooltip.style.top = rect.top + window.scrollY - margin + "px";
	} else {
		tooltip.style.transform = "translateX(-50%)";
		tooltip.style.top = rect.bottom + window.scrollY + margin + "px";
	}
	tooltip.style.left = left + "px";
}

let tooltipHideTimer = null;
let tooltipShowTimer = null;

function cancelHideTooltip() {
	if (tooltipHideTimer) {
		clearTimeout(tooltipHideTimer);
		tooltipHideTimer = null;
	}
}

function cancelShowTooltip() {
	if (tooltipShowTimer) {
		clearTimeout(tooltipShowTimer);
		tooltipShowTimer = null;
	}
}

function hideTooltip() {
	cancelHideTooltip();
	const tooltip = document.getElementById("timetable-tooltip");
	if (tooltip) {
		tooltip.remove();
	}
}

// Hover intent: only show the tooltip once the pointer has rested on an event
// for a moment. This way, briefly crossing a neighbouring event (e.g. a stacked
// event in month view) on the way to the current tooltip does not swap it.
function scheduleShowTooltip(info) {
	cancelShowTooltip();
	tooltipShowTimer = setTimeout(() => {
		tooltipShowTimer = null;
		showTooltip(info);
	}, 250);
}

// Delay hiding so the pointer can travel from the event onto the tooltip
// (e.g. to scroll a long participation list) without it disappearing. Also
// cancels a pending show, so passing over an event en route is a no-op.
function scheduleHideTooltip() {
	cancelShowTooltip();
	cancelHideTooltip();
	tooltipHideTimer = setTimeout(hideTooltip, 300);
}

// Small hint icon on events that have a Bemerkung set, so it's visible without hovering.
function markDescription(info) {
	if (info.event.extendedProps.description) {
		const icon = document.createElement("i");
		icon.className = "fa-solid fa-comment tt-description-icon";
		info.el.appendChild(icon);
	}
}

// --- Click ------------------------------------------------------------------
function handleEventClick(info) {
	// Open the course's appointment overview in a new tab instead of navigating
	// away from the timetable.
	info.jsEvent.preventDefault();
	if (info.event.url) {
		window.open(info.event.url, "_blank", "noopener");
	}
}

// --- Date picker ------------------------------------------------------------
const datePickerState = ref({
	isVisible: false,
	currentDate: new Date(),
});

function showDatePicker() {
	let currentDate = new Date();
	const view = calendar.value?.getApi()?.view;
	if (view && view.activeStart) {
		currentDate = view.activeStart;
	}
	datePickerState.value = { isVisible: true, currentDate };
}

function closeDatePicker() {
	datePickerState.value.isVisible = false;
}

function handleDateSelected(dateString) {
	calendar.value?.getApi()?.gotoDate(dateString);
}

// --- Calendar ---------------------------------------------------------------
const calendar = ref(null);

const calendarOptions = computed(() => ({
	plugins: [timeGridPlugin, dayGridPlugin],
	locale: deLocale,
	initialView: "timeGridWeek",
	headerToolbar: {
		left: "title",
		center: "",
		right: "prev,next,today,datePicker,dayGridMonth,timeGridWeek,timeGridDay",
	},
	customButtons: {
		datePicker: {
			text: "📅 Datum",
			hint: "Zu Datum springen",
			click: showDatePicker,
		},
	},
	firstDay: 1,
	allDaySlot: true,
	slotMinTime: "07:00:00",
	slotMaxTime: "23:00:00",
	nowIndicator: true,
	height: "auto",
	expandRows: true,
	stickyHeaderDates: true,
	// Render timed events as filled blocks in every view; without this the month (dayGrid) view shows
	// them as a dot + coloured text instead of a filled background, hiding the booking-status colour.
	eventDisplay: "block",
	events: events.value,
	datesSet: handleDatesSet,
	eventClick: handleEventClick,
	eventMouseEnter: scheduleShowTooltip,
	eventMouseLeave: scheduleHideTooltip,
	eventDidMount: markDescription,
}));

return (_ctx, _cache) => {
  const _component_VMultiselect = _resolveComponent("VMultiselect")

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createCommentVNode(" Filter bar "),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(filterDefs, (def) => {
            return _createElementVNode("div", {
              key: def.key,
              class: "col-6 col-md-4 col-lg"
            }, [
              _createElementVNode("label", _hoisted_5, _toDisplayString(def.label), 1 /* TEXT */),
              _createVNode(_component_VMultiselect, {
                modelValue: selected[def.key],
                "onUpdate:modelValue": $event => ((selected[def.key]) = $event),
                options: options[def.key] || [],
                multiple: true,
                "close-on-select": false,
                "clear-on-select": false,
                label: "label",
                "track-by": "value",
                placeholder: "Alle",
                "select-label": "Auswählen",
                "selected-label": "Ausgewählt",
                "deselect-label": "Entfernen"
              }, null, 8 /* PROPS */, ["modelValue", "onUpdate:modelValue", "options"])
            ])
          }), 64 /* STABLE_FRAGMENT */))
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-primary",
            disabled: loading.value,
            onClick: loadWithCurrentFilters
          }, [...(_cache[2] || (_cache[2] = [
            _createElementVNode("i", { class: "fa-solid fa-magnifying-glass me-1" }, null, -1 /* CACHED */),
            _createTextVNode(" Laden ", -1 /* CACHED */)
          ]))], 8 /* PROPS */, _hoisted_7),
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-outline-secondary",
            disabled: loading.value,
            onClick: resetFilters
          }, " Zurücksetzen ", 8 /* PROPS */, _hoisted_8),
          (loading.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_9, [...(_cache[3] || (_cache[3] = [
                _createElementVNode("i", { class: "fa-solid fa-spinner fa-spin me-1" }, null, -1 /* CACHED */),
                _createTextVNode(" Lade Termine … ", -1 /* CACHED */)
              ]))]))
            : (appliedFilters.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(events.value.length) + " Termin(e) ", 1 /* TEXT */))
              : _createCommentVNode("v-if", true)
        ])
      ])
    ]),
    _createElementVNode("ul", _hoisted_11, [
      _createElementVNode("li", _hoisted_12, [
        _createElementVNode("button", {
          type: "button",
          class: _normalizeClass(["nav-link", { active: activeTab.value === 'calendar' }]),
          onClick: _cache[0] || (_cache[0] = $event => (setTab('calendar')))
        }, [...(_cache[4] || (_cache[4] = [
          _createElementVNode("i", { class: "fa-solid fa-calendar-days me-1" }, null, -1 /* CACHED */),
          _createTextVNode(" Kalender ", -1 /* CACHED */)
        ]))], 2 /* CLASS */)
      ]),
      _createElementVNode("li", _hoisted_13, [
        _createElementVNode("button", {
          type: "button",
          class: _normalizeClass(["nav-link", { active: activeTab.value === 'conflicts' }]),
          onClick: _cache[1] || (_cache[1] = $event => (setTab('conflicts')))
        }, [
          _cache[5] || (_cache[5] = _createElementVNode("i", { class: "fa-solid fa-triangle-exclamation me-1" }, null, -1 /* CACHED */)),
          _cache[6] || (_cache[6] = _createTextVNode(" Konflikte ", -1 /* CACHED */)),
          (conflictSummary.value.total)
            ? (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(conflictSummary.value.total), 1 /* TEXT */))
            : _createCommentVNode("v-if", true)
        ], 2 /* CLASS */)
      ])
    ]),
    _withDirectives(_createElementVNode("div", null, [
      (!appliedFilters.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_15, [...(_cache[7] || (_cache[7] = [
            _createTextVNode(" Bitte Filter wählen und auf ", -1 /* CACHED */),
            _createElementVNode("strong", null, "Laden", -1 /* CACHED */),
            _createTextVNode(" klicken, um die Termine anzuzeigen. ", -1 /* CACHED */)
          ]))]))
        : (_openBlock(), _createElementBlock("div", _hoisted_16, [...(_cache[8] || (_cache[8] = [
            _createStaticVNode("<div class=\"d-flex flex-wrap align-items-center gap-3 mb-1\"><span><span class=\"tt-status-legend tt-status-legend--planned me-1\"></span> geplant</span><span><span class=\"tt-status-legend tt-status-legend--booked me-1\"></span> gebucht</span></div><div class=\"d-flex flex-wrap align-items-center gap-3\"><span>Umrandete Termine haben einen Konflikt:</span><span><span class=\"tt-conflict-legend tt-conflict-legend--room me-1\"></span> Raum</span><span><span class=\"tt-conflict-legend tt-conflict-legend--lecturer me-1\"></span> Dozent*in</span><span><span class=\"tt-conflict-legend tt-conflict-legend--cohort me-1\"></span> Kohorte</span></div>", 2)
          ]))])),
      _createVNode(_unref(FullCalendar), {
        ref_key: "calendar",
        ref: calendar,
        options: calendarOptions.value
      }, null, 8 /* PROPS */, ["options"])
    ], 512 /* NEED_PATCH */), [
      [_vShow, activeTab.value === 'calendar']
    ]),
    _withDirectives(_createElementVNode("div", null, [
      _createVNode(ConflictOverview, {
        conflicts: conflicts.value,
        summary: conflictSummary.value,
        loading: conflictsLoading.value,
        "applied-filters": !!appliedFilters.value,
        onJumpToDate: handleJumpToDate
      }, null, 8 /* PROPS */, ["conflicts", "summary", "loading", "applied-filters"])
    ], 512 /* NEED_PATCH */), [
      [_vShow, activeTab.value === 'conflicts']
    ]),
    _createVNode(CalendarDatePicker, {
      "is-visible": datePickerState.value.isVisible,
      "current-date": datePickerState.value.currentDate,
      onClose: closeDatePicker,
      onDateSelected: handleDateSelected
    }, null, 8 /* PROPS */, ["is-visible", "current-date"])
  ]))
}
}

}